import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AuthStatus, Gender } from '../types/enums';
import { AuthContextState, LoginCredentials, RegisterCredentials, GoogleAuthResult, ProfileCompletionData, User, AuthProviderProps } from '../types/Auth.types';
import authService from '../services/auth.service';
import { ApiError, removeToken } from '../services/api';
import { queryClient } from '../utils/queryClient';
import { ERROR_MESSAGES } from '../constants/stringConstants';
import { mapGenderToApi } from '../utils/apiMappers';

// Create the auth context
const AuthContext = createContext<AuthContextState | undefined>(undefined);

/**
 * AuthProvider component to wrap the app
 * 
 * Features:
 * - Real authentication using backend API
 * - Login with email/password validation
 * - JWT token management
 * - Logout with state cleanup
 * - Automatic token validation on mount
 * - LocalStorage persistence
 * 
 * Usage:
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING);

  /**
   * Initialize auth state from stored token
   */
  useEffect(() => {
    const initAuth = async () => {
      if (authService.hasValidToken()) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          setStatus(AuthStatus.AUTHENTICATED);
        } catch (error) {
          // Token invalid or expired
          authService.logout();
          setUser(null);
          setStatus(AuthStatus.UNAUTHENTICATED);
        }
      } else {
        setStatus(AuthStatus.UNAUTHENTICATED);
      }
    };

    initAuth();
  }, []);

  /**
   * Login function - calls backend API
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    setStatus(AuthStatus.LOADING);

    try {
      const authenticatedUser = await authService.login(credentials);
      setUser(authenticatedUser);
      setStatus(AuthStatus.AUTHENTICATED);
    } catch (error) {
      setStatus(AuthStatus.UNAUTHENTICATED);
      
      // Transform API error to user-friendly message
      const apiError = error as ApiError;
      throw new Error(apiError.message || ERROR_MESSAGES.LOGIN_FAILED);
    }
  }, []);

  /**
   * Logout function - clears user and auth state
   */
  const logout = useCallback(async () => {
    // 1. Kill token FIRST — prevents hooks from refetching with old credentials on remount
    removeToken();
    // 2. Nuke React Query cache
    queryClient.clear();
    // 3. Clear state — triggers component remount via key={user?.id}
    setUser(null);
    setStatus(AuthStatus.UNAUTHENTICATED);
    // 4. Best-effort backend session invalidation (token already gone, may 401)
    try {
      await authService.logout();
    } catch {
      // Expected — token was already cleared
    }
  }, []);

  /**
   * Register a new doctor
   */
  const register = useCallback(async (credentials: RegisterCredentials): Promise<void> => {
    setStatus(AuthStatus.LOADING);

    try {
      await authService.register({
        name: credentials.name,
        email: credentials.email,
        password: credentials.password,
        gender: mapGenderToApi(credentials.gender as Gender),
        phone: credentials.phone || undefined,
        role: credentials.role,
        specialization: credentials.specialization,
        licenseNumber: credentials.licenseNumber,
      });
      // Re-fetch via /auth/me so the user object contains every persisted
      // field (gender, phone, doctor profile, etc.) — register's response
      // intentionally returns only the minimal subset.
      const fullUser = await authService.getCurrentUser();
      setUser(fullUser);
      setStatus(AuthStatus.AUTHENTICATED);
    } catch (error) {
      setStatus(AuthStatus.UNAUTHENTICATED);
      const apiError = error as ApiError;
      throw new Error(apiError.message || ERROR_MESSAGES.REGISTRATION_FAILED);
    }
  }, []);

  /**
   * Google OAuth login. Optional `intentRole` is forwarded to the backend
   * so a Google sign-up from /patient/signup creates a Patient row (and
   * skips the doctor profile-completion screen entirely).
   */
  const googleLogin = useCallback(async (
    credential: string,
    intentRole?: 'DOCTOR' | 'PATIENT'
  ): Promise<GoogleAuthResult> => {
    setStatus(AuthStatus.LOADING);

    try {
      const result = await authService.googleAuth({
        credential,
        profileData: intentRole ? { role: intentRole } : undefined,
      });

      if (result.token && !result.needsProfileCompletion) {
        // Re-fetch full user so the stored object includes name, email, role,
        // and any backend-assigned IDs even if Google's id_token didn't carry
        // the freshly-stored gender/phone/etc.
        try {
          const fullUser = await authService.getCurrentUser();
          setUser(fullUser);
        } catch {
          // Fall back to the minimal subset returned by /auth/google.
          const googleUser: User = {
            id: result.user.id,
            email: result.user.email,
            name: result.user.name,
            role: result.user.role || intentRole || 'DOCTOR',
            doctorId: result.user.doctorId,
            patientId: result.user.patientId,
          };
          setUser(googleUser);
        }
        setStatus(AuthStatus.AUTHENTICATED);
      } else {
        setStatus(AuthStatus.UNAUTHENTICATED);
      }

      return result;
    } catch (error) {
      setStatus(AuthStatus.UNAUTHENTICATED);
      const apiError = error as ApiError;
      throw new Error(apiError.message || ERROR_MESSAGES.GOOGLE_AUTH_FAILED);
    }
  }, []);

  /**
   * Complete profile after Google OAuth
   */
  const completeProfile = useCallback(async (data: ProfileCompletionData): Promise<void> => {
    setStatus(AuthStatus.LOADING);

    try {
      const mappedData = {
        ...data,
        gender: data.gender ? mapGenderToApi(data.gender as Gender) : undefined,
      };
      await authService.completeProfile(mappedData);
      // Re-fetch full user (same reasoning as `register`).
      const fullUser = await authService.getCurrentUser();
      setUser(fullUser);
      setStatus(AuthStatus.AUTHENTICATED);
    } catch (error) {
      setStatus(AuthStatus.UNAUTHENTICATED);
      const apiError = error as ApiError;
      throw new Error(apiError.message || ERROR_MESSAGES.PROFILE_COMPLETION_FAILED);
    }
  }, []);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = status === AuthStatus.AUTHENTICATED;

  /**
   * Check if auth is loading
   */
  const isLoading = status === AuthStatus.LOADING;

  const value: AuthContextState = {
    user,
    status,
    login,
    register,
    googleLogin,
    completeProfile,
    logout,
    isAuthenticated,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
